import { useEffect, useRef } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { useVoiceInput } from "../../hooks/useVoiceInput";
import { useI18n } from "../../i18n";

interface VoiceInputButtonProps {
  /** Called with recognized text */
  onTranscript: (text: string) => void;
  disabled?: boolean;
  lang?: string;
}

export default function VoiceInputButton({
  onTranscript,
  disabled,
  lang,
}: VoiceInputButtonProps) {
  const { t } = useI18n();
  const voiceInput = useVoiceInput({ lang });
  const prevTranscriptRef = useRef("");

  // Deliver transcript when it changes and state transitions from processing
  useEffect(() => {
    if (
      voiceInput.transcript &&
      voiceInput.transcript !== prevTranscriptRef.current
    ) {
      prevTranscriptRef.current = voiceInput.transcript;
      onTranscript(voiceInput.transcript);
      // Reset for next input
      voiceInput.reset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceInput.transcript, onTranscript]);

  // Show toast on specific voice errors
  useEffect(() => {
    if (!voiceInput.errorType) return;
    if (voiceInput.errorType === 'not-allowed') {
      toast.error(t('aiError.voicePermissionDenied'));
    } else if (voiceInput.errorType === 'audio-capture') {
      toast.error(t('aiError.voiceNotAvailable'));
    }
  }, [voiceInput.errorType, t]);

  if (!voiceInput.isSupported) {
    return null;
  }

  const handleClick = () => {
    if (voiceInput.state === "listening") {
      voiceInput.stop();
    } else if (voiceInput.state === "idle" || voiceInput.state === "error") {
      voiceInput.start();
    }
    // Don't do anything during "processing"
  };

  const getTitle = (): string => {
    switch (voiceInput.state) {
      case "listening":
        return t("mobile.voiceListening");
      case "error":
        return t("mobile.voiceError");
      default:
        return t("mobile.voiceInput");
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      disabled={disabled || voiceInput.state === "processing"}
      className={cn(
        "voice-input-btn",
        voiceInput.state === "listening" && "voice-input-btn--listening",
      )}
      title={getTitle()}
      aria-label={getTitle()}
    >
      {voiceInput.state === "listening" ? (
        <MicOff size={16} className="text-danger animate-pulse" />
      ) : voiceInput.state === "processing" ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <Mic size={16} />
      )}
    </Button>
  );
}
